import {
    Bar,
    BarChart,
    CartesianGrid,
    Label,
    ResponsiveContainer,
    ReferenceArea,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import BasicChartTooltip from "components-for/charts/basic-chart-tooltip";
import { Box, BoxContent, BoxHeader, BoxHeading, BoxSubheading } from "@edgetier/client-components";
import { formatNumericTickValue } from "utilities/format-numeric-tick-value";
import { FunctionComponent, memo } from "react";
import { IProps } from "./metric-values-chart.types";
import { Button, SpinnerUntil } from "@edgetier/components";
import { useAgentFirstName } from "hooks-for/agents/use-agent-first-name";
import { useInteractionMetricConversion } from "hooks-for/metrics/use-interaction-metric-conversion";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useMetricDistribution } from "hooks-for/metrics/use-metric-distributions";

import { useAgentChartSelection } from "hooks-for/agents/use-agent-chart-selection";
import MetricChartValuesRange from "./metric-chart-values-range";
import { faUndo } from "@fortawesome/pro-solid-svg-icons";
import { IMetricChartFilterSelection } from "types-for/charts/metric-chart.types";
import { SUBSTRACT_AMOUNT } from "./metric-values-chart.constants";
import "./metric-values-chart.scss";
import { IInteractionMetricsDistribution, IInteractionMetricsDistributions } from "@edgetier/watchtower-types";
import { isBooleanMetric } from "utilities-for/metrics/is-boolean-metric";
import { getMetricDistributionStartAndEndValue } from "utilities-for/metrics/get-metric-distributions-start-and-end-value";

/**
 * Show a bar chart of an agent(s) values for a given metric.
 * @param props.defaultEndDateTime   Closing date in search range.
 * @param props.defaultStartDateTime Opening date in search range.
 * @param props.height               Optional height (mainly for testing).
 * @param props.metric               Metric to chart.
 * @returns                          Histogram/bar chart of an agent's metric values.
 */
const MetricValuesChart: FunctionComponent<IProps> = ({
    defaultEndDateTime,
    defaultStartDateTime,
    height = "100%",
    metric,
    reviewAgentId,
    width = "100%",
}) => {
    const filters = useInteractionQueryParameters({
        defaults: { startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime },
    });

    const { data: metricDistributionData, isLoading } = useMetricDistribution(metric, {
        agentId: reviewAgentId,
        ...filters,
    });

    // The metrics all have different units so might have to be converted from seconds to minutes, percentages etc.
    const { axisLabel, formatTick, metricLabel } = useInteractionMetricConversion<
        IInteractionMetricsDistribution<typeof metric>
    >(metric, {
        isAggregate: false,
        items: metricDistributionData ?? [],
        getValue: (item) => ("minimumValue" in item ? item.minimumValue : item.value),
    });

    // Get the agent's name for labels etc.
    const { data: agentFirstName, isLoading: isAgentFirstNameLoading } = useAgentFirstName(reviewAgentId ?? "", {
        enabled: typeof reviewAgentId === "string",
    });

    /**
     * Substract the a very small amount from right side of metric filter selection
     */
    const updateMetricFilterValues = (filterSelection: [IMetricChartFilterSelection, IMetricChartFilterSelection]) => {
        const [left, right] = filterSelection;
        if (isBooleanMetric(metric)) {
            return [left, right];
        }
        return [left, { ...right, activeValue: right.activeValue - SUBSTRACT_AMOUNT }];
    };

    const { filterSelection, mouseEventCallbacks, resetChartFilter } = useAgentChartSelection({
        ...getMetricDistributionStartAndEndValue(metricDistributionData),
        metric,
        beforeUpdateUrl: updateMetricFilterValues,
    });

    return (
        <Box className="metric-values-chart">
            <BoxHeader>
                <BoxHeading>{metricLabel} Distribution</BoxHeading>
                {typeof reviewAgentId === "string" && (
                    <BoxSubheading>
                        {isAgentFirstNameLoading && <>&hellip;</>}
                        {!isAgentFirstNameLoading && (
                            <>
                                {agentFirstName}'s {metricLabel.toLowerCase()} distribution
                            </>
                        )}
                    </BoxSubheading>
                )}
            </BoxHeader>
            <BoxContent>
                <SpinnerUntil<IInteractionMetricsDistributions<typeof metric>>
                    data={metricDistributionData}
                    isReady={!isLoading}
                >
                    {(chartData) =>
                        typeof chartData === "undefined" ? (
                            <div>Error loading chart data.</div>
                        ) : (
                            <>
                                <div className="metric-values-chart__filter-info">
                                    <MetricChartValuesRange
                                        startValue={filterSelection[0].activeValue}
                                        endValue={filterSelection[1].activeValue}
                                        isBooleanRange={isBooleanMetric(metric)}
                                        formatValue={formatTick}
                                    />
                                    <Button
                                        disabled={filterSelection.some(({ activeLabel }) => activeLabel === null)}
                                        icon={faUndo}
                                        onClick={() => resetChartFilter()}
                                        isIconOnly
                                    />
                                </div>
                                <ResponsiveContainer height={height} width={width}>
                                    <BarChart
                                        data={chartData}
                                        margin={{ bottom: 10, left: 10 }}
                                        {...mouseEventCallbacks}
                                    >
                                        <CartesianGrid />
                                        <Bar dataKey="frequency" radius={5} />
                                        <XAxis
                                            // Use a tick component instead of dataKey and tickFormatter because dataKey
                                            // will reutrn an empty string for boolean values.
                                            tick={({ x, y, payload }) => {
                                                const item = chartData?.[payload.index] ?? {};
                                                const value = "value" in item ? item.value : item.minimumValue;
                                                return (
                                                    <text x={x} y={y + 10} textAnchor="middle">
                                                        {formatTick(value as number)}
                                                    </text>
                                                );
                                            }}
                                        >
                                            <Label
                                                offset={-5}
                                                position="insideBottom"
                                                style={{ textAnchor: "middle" }}
                                                value={axisLabel}
                                            />
                                        </XAxis>
                                        <YAxis tickFormatter={formatNumericTickValue}>
                                            <Label
                                                angle={-90}
                                                offset={0}
                                                value="Interactions"
                                                position="insideLeft"
                                                style={{ textAnchor: "middle" }}
                                            />
                                        </YAxis>
                                        <ReferenceArea
                                            fill={"#1abc9c"}
                                            fillOpacity={0.3}
                                            x1={filterSelection[0].activeLabel ?? ""}
                                            x2={filterSelection[1].activeLabel ?? ""}
                                        />
                                        <Tooltip
                                            isAnimationActive={false}
                                            content={({ active, payload }) =>
                                                active &&
                                                Array.isArray(payload) &&
                                                typeof payload[0].value === "number" && (
                                                    <BasicChartTooltip
                                                        frequency={payload[0].payload.frequency}
                                                        label="interactions"
                                                    />
                                                )
                                            }
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </>
                        )
                    }
                </SpinnerUntil>
            </BoxContent>
        </Box>
    );
};

export default memo(MetricValuesChart);
